/** @jsxImportSource @emotion/react */
import { StatisticsDateRangePicker } from '../../components/StatisticsDateRangePicker';
import { css } from '@emotion/react';
import { useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type EChartsReactCore from 'echarts-for-react/lib/core';
import {
  Card,
  Select,
  Button,
  Typography,
  Space,
  Spin,
  Checkbox
} from 'antd';
import {
  ChartDataItem,
  ViewByType,
  StackedChartData
} from '../../services/statistics.service';
import { HEALTH_STATUS_OPTIONS } from '../../../../constants/health-status.constant';
import {
  COLOR_PALETTE_20,
  AGE_GROUP_COLORS,
  PROVINCE_COLORS,
  PROVINCE_PALETTE,
  GENDER_COLORS,
  PRIMARY_BAR_COLOR,
  formatNumber,
  formatCategoryAxisLabel,
  ALL_PROVINCE_OPTIONS,
  AGE_GROUPS_OPTIONS,
} from '../../constants';
import { removeVietnameseTones } from '../../../../utils/common';
import { BAR_LABEL_FONT_SIZE, fitBarLabel } from '../../utils/barLabelLayout';

const { Title, Text } = Typography;

interface ProfileChartsProps {
  loading?: boolean;
  viewBy: ViewByType;
  subFilter: string | string[];
  onSubFilterChange: (val: string | string[]) => void;
  subFilter2?: string | string[];
  onSubFilter2Change?: (val: string | string[]) => void;
  period: 'today' | 'this_month' | 'this_year' | 'all' | 'custom';
  onPeriodChange: (
    val: 'today' | 'this_month' | 'this_year' | 'all' | 'custom'
  ) => void;
  dateRange: [string | null, string | null];
  onDateRangeChange: (range: [string | null, string | null]) => void;
  chartData: ChartDataItem[];
  stackedChartData?: StackedChartData;
  totalProfiles: number;
  target: 'user' | 'relative' | 'all';
}

export interface ProfileChartsHandle {
  /** Returns Base64 PNG data URL of the current ECharts chart, or null if unavailable */
  getChartImage: () => string | null;
}


/**
 * Helper to compare if two combination strings (e.g. "Gút - Thừa cân" and "Thừa cân - Gút")
 * contain the exact same set of components regardless of order.
 */
const areCombinationsEquivalent = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  const partsA = a.split(/[-–—,;/]+/).map((p) => p.trim().toLowerCase()).filter(Boolean).sort();
  const partsB = b.split(/[-–—,;/]+/).map((p) => p.trim().toLowerCase()).filter(Boolean).sort();
  if (partsA.length !== partsB.length || partsA.length <= 1) return false;
  return partsA.every((p, idx) => p === partsB[idx]);
};

/**
 * Custom search matcher for Select dropdown filters.
 * Matches multi-term combinations in any order (e.g. typing "Gút - Thừa Cân" matches "Thừa cân - Gút...").
 * Also supports accent-insensitive and word-by-word token matching.
 */
const matchSearchOption = (input: string, option: any): boolean => {
  if (!input || !input.trim()) return true;

  const rawInput = input.trim().toLowerCase();
  const rawLabel = (option?.label ?? '').toString().toLowerCase();
  const rawVal = (option?.value ?? '').toString().toLowerCase();
  const rawDb = (option?.dbName ?? '').toString().toLowerCase();

  // 1. Direct exact substring match
  if (rawLabel.includes(rawInput) || rawVal.includes(rawInput) || (rawDb && rawDb.includes(rawInput))) {
    return true;
  }

  // 2. Normalized without Vietnamese tones
  const normInput = removeVietnameseTones(rawInput);
  const normLabel = removeVietnameseTones(rawLabel);
  const normVal = removeVietnameseTones(rawVal);
  const normDb = rawDb ? removeVietnameseTones(rawDb) : '';

  if (normLabel.includes(normInput) || normVal.includes(normInput) || (normDb && normDb.includes(normInput))) {
    return true;
  }

  // 3. Multi-phrase tokens separated by delimiters (-, ,, ;, /)
  // e.g. "Gút - Thừa Cân" -> ["gút", "thừa cân"]
  const delimiterTokens = rawInput
    .split(/[-–—,;/]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  if (delimiterTokens.length > 0) {
    const allTokensMatch = delimiterTokens.every((token) => {
      const normToken = removeVietnameseTones(token);
      return (
        rawLabel.includes(token) ||
        rawVal.includes(token) ||
        (rawDb && rawDb.includes(token)) ||
        normLabel.includes(normToken) ||
        normVal.includes(normToken) ||
        (normDb && normDb.includes(normToken))
      );
    });
    if (allTokensMatch) return true;
  }

  // 4. Word-by-word tokens separated by whitespace or delimiters
  // e.g. "Gút Thừa Cân" -> ["gut", "thua", "can"]
  const wordTokens = normInput
    .split(/[\s\-–—,;/]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  if (wordTokens.length > 0) {
    const allWordsMatch = wordTokens.every((w) => {
      return normLabel.includes(w) || normVal.includes(w) || (normDb && normDb.includes(w));
    });
    if (allWordsMatch) return true;
  }

  return false;
};

const isProvinceMatch = (p1?: string, p2?: string): boolean => {
  if (!p1 || !p2) return p1 === p2;
  if (p1 === p2) return true;
  const norm = (s: string) =>
    s
      .replace(/^(tp\.?|thành phố|tỉnh)\s*/i, '')
      .replace(/đắc/gi, 'đắk')
      .trim()
      .toLowerCase();
  return norm(p1) === norm(p2);
};

export const ProfileCharts = forwardRef<ProfileChartsHandle, ProfileChartsProps>((
{
  loading = false,
  viewBy,
  subFilter,
  onSubFilterChange,
  subFilter2 = 'all',
  onSubFilter2Change,
  period,
  onPeriodChange,
  dateRange,
  onDateRangeChange,
  chartData,
  stackedChartData,
  target
}, ref) => {
  const echartsRef = useRef<EChartsReactCore>(null);

  useImperativeHandle(ref, () => ({
    getChartImage: () => {
      try {
        const instance = echartsRef.current?.getEchartsInstance();
        if (!instance) return null;
        return instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
      } catch {
        return null;
      }
    },
  }));
  const getTargetLabel = () => {
    switch (target) {
      case 'relative':
        return 'người thân';
      case 'all':
        return 'người dùng và người thân';
      case 'user':
      default:
        return 'người dùng';
    }
  };

  const getChartTitle = () => {
    const targetLabel = getTargetLabel();
    switch (viewBy) {
      case 'age':
        return `Độ tuổi của ${targetLabel}`;
      case 'gender':
        return `Giới tính của ${targetLabel}`;
      case 'province':
        return `Tỉnh thành của ${targetLabel}`;
      case 'health_status':
        return `Tình trạng sức khỏe của ${targetLabel}`;
      case 'age_gender':
        return `Độ tuổi và giới tính của ${targetLabel}`;
      case 'age_province':
        return `Độ tuổi và tỉnh thành của ${targetLabel}`;
      case 'age_health':
        return `Độ tuổi và tình trạng sức khỏe của ${targetLabel}`;
      case 'health_gender':
        return `Tình trạng sức khỏe và giới tính của ${targetLabel}`;
      case 'province_gender':
        return `Tỉnh thành và giới tính của ${targetLabel}`;
      case 'province_health':
        return `Tỉnh thành và tình trạng sức khỏe của ${targetLabel}`;
      case 'custom':
        return 'Danh sách tuỳ chỉnh';
      default:
        return `Thống kê ${targetLabel}`;
    }
  };

  const getTotalLabel = () => {
    switch (target) {
      case 'relative':
        return `Số người thân có trong hệ thống`;
      case 'all':
        return `Số người dùng và người thân có trong hệ thống`;
      case 'user':
      default:
        return `Số người dùng có trong hệ thống`;
    }
  }

  const filteredChartData = useMemo(() => {
    if (subFilter === 'all' || (Array.isArray(subFilter) && subFilter.length === 0)) {
      return chartData;
    }
    const filterArr = Array.isArray(subFilter) ? subFilter : [subFilter];
    const filtered = chartData.filter((item) => {
      return filterArr.some((f) => {
        if (f === 'all') return true;
        if (item.code && item.code === f) return true;
        if (item.name && item.name === f) return true;
        if (item.name && isProvinceMatch(item.name, f)) return true;
        const hs = HEALTH_STATUS_OPTIONS.find((h) => h.value === f);
        if (hs) {
          if (item.code === hs.value) return true;
          if (item.name === hs.label || areCombinationsEquivalent(item.name, hs.label)) return true;
          if (hs.dbName && (item.name === hs.dbName || areCombinationsEquivalent(item.name, hs.dbName))) return true;
        }
        return false;
      });
    });

    filterArr.forEach((f) => {
      if (f === 'all') return;
      const alreadyIncluded = filtered.some((item) => {
        if (item.code && item.code === f) return true;
        if (item.name && item.name === f) return true;
        if (item.name && isProvinceMatch(item.name, f)) return true;
        const hs = HEALTH_STATUS_OPTIONS.find((h) => h.value === f);
        if (hs) {
          if (item.code === hs.value) return true;
          if (item.name === hs.label || areCombinationsEquivalent(item.name, hs.label)) return true;
          if (hs.dbName && (item.name === hs.dbName || areCombinationsEquivalent(item.name, hs.dbName))) return true;
        }
        return false;
      });

      if (!alreadyIncluded) {
        const hs = HEALTH_STATUS_OPTIONS.find((h) => h.value === f);
        const ageMatch = AGE_GROUPS_OPTIONS.find((a) => a.value === f);
        const provMatch = ALL_PROVINCE_OPTIONS.find((p) => p.value === f || isProvinceMatch(p.value, f));
        const label = hs
          ? hs.label
          : ageMatch
          ? ageMatch.label
          : provMatch
          ? provMatch.label
          : f === 'MALE'
          ? 'Nam'
          : f === 'FEMALE'
          ? 'Nữ'
          : f === 'NO_INFO'
          ? 'Không có thông tin'
          : String(f);
        filtered.push({
          code: String(f),
          name: label,
          value: 0,
          percentage: 0
        });
      }
    });

    return filtered;
  }, [subFilter, chartData]);

  const isCombinedView = [
    'age_gender',
    'age_province',
    'age_health',
    'health_gender',
    'province_gender',
    'province_health'
  ].includes(viewBy);

  const getOption = () => {
    // Stacked Bar Chart (for combined views like age_gender, age_province, etc.)
    if (
      (isCombinedView && stackedChartData) ||
      (stackedChartData &&
        stackedChartData.categories &&
        stackedChartData.categories.length > 0)
    ) {
      let displayCategories = [...(stackedChartData.categories || [])];
      let displaySeries = (stackedChartData.series || []).map((s) => ({
        ...s,
        data: [...s.data]
      }));

      const normalizeFilterValue = (val: any): { key: string; aliases: string[] }[] => {
        if (!val || val === 'all') return [];
        if (Array.isArray(val)) {
          return val.flatMap((v) => normalizeFilterValue(v));
        }
        if (val === 'MALE') return [{ key: 'Nam', aliases: ['Nam'] }];
        if (val === 'FEMALE') return [{ key: 'Nữ', aliases: ['Nữ'] }];
        if (val === 'NO_INFO') return [{ key: 'Không có thông tin', aliases: ['Không có thông tin'] }];

        const ageMatch = AGE_GROUPS_OPTIONS.find((a) => a.value === val);
        if (ageMatch) {
          return [{ key: ageMatch.label, aliases: [ageMatch.label] }];
        }

        const healthMatch = HEALTH_STATUS_OPTIONS.find((h) => h.value === val);
        if (healthMatch) {
          const aliases = [healthMatch.label];
          if (healthMatch.dbName && healthMatch.dbName !== healthMatch.label) {
            aliases.push(healthMatch.dbName);
          }
          return [{ key: healthMatch.label, aliases }];
        }

        const provMatch = ALL_PROVINCE_OPTIONS.find((p) => p.value === val || isProvinceMatch(p.value, val));
        if (provMatch) {
          const aliases = [provMatch.label];
          if (provMatch.label === 'TP Hồ Chí Minh') aliases.push('Hồ Chí Minh');
          if (provMatch.label === 'Đắk Lắk') aliases.push('Đắc Lắk');
          if (provMatch.label === 'Đắk Nông') aliases.push('Đắc Nông');
          return [{ key: provMatch.label, aliases }];
        }

        return [{ key: String(val), aliases: [String(val)] }];
      };

      const f1Items = normalizeFilterValue(subFilter);
      const f2Items = normalizeFilterValue(subFilter2);

      let categoryFilterItems: { key: string; aliases: string[] }[] = [];
      let seriesFilterItems: { key: string; aliases: string[] }[] = [];

      if (['age_gender', 'health_gender', 'province_gender'].includes(viewBy)) {
        categoryFilterItems = f1Items;
        seriesFilterItems = f2Items;
      } else if (['age_province', 'age_health', 'province_health'].includes(viewBy)) {
        categoryFilterItems = f2Items;
        seriesFilterItems = f1Items;
      }

      // 1. Filter Categories if categoryFilterItems is specified
      if (categoryFilterItems.length > 0) {
        const newCategories: string[] = [];
        const matchingIndices: number[] = [];

        displayCategories.forEach((cat, idx) => {
          const matches = categoryFilterItems.some((item) =>
            item.aliases.some((alias) => alias === cat || areCombinationsEquivalent(alias, cat) || isProvinceMatch(alias, cat))
          );
          if (matches) {
            newCategories.push(cat);
            matchingIndices.push(idx);
          }
        });

        // If a selected category is not in displayCategories (e.g. 0 count in backend), still show it with 0
        categoryFilterItems.forEach((item) => {
          const alreadyExists = newCategories.some((cat) =>
            item.aliases.some((alias) => alias === cat || areCombinationsEquivalent(alias, cat) || isProvinceMatch(alias, cat))
          );
          if (!alreadyExists) {
            newCategories.push(item.key);
            matchingIndices.push(-1);
          }
        });

        displayCategories = newCategories;
        displaySeries = displaySeries.map((s) => ({
          ...s,
          data: matchingIndices.map((idx) => (idx >= 0 ? s.data[idx] : 0))
        }));
      }

      // 2. Filter Series if seriesFilterItems is specified
      if (seriesFilterItems.length > 0) {
        const newSeries: typeof displaySeries = [];

        seriesFilterItems.forEach((item) => {
          const existing = displaySeries.find((s) =>
            item.aliases.some((alias) => alias === s.name || areCombinationsEquivalent(alias, s.name) || isProvinceMatch(alias, s.name))
          );
          if (existing) {
            if (!newSeries.some((s) => s.name === existing.name)) {
              newSeries.push(existing);
            }
          } else {
            const alreadyAdded = newSeries.some((s) =>
              item.aliases.some((alias) => alias === s.name || areCombinationsEquivalent(alias, s.name) || isProvinceMatch(alias, s.name))
            );
            if (!alreadyAdded) {
              newSeries.push({
                name: item.key,
                data: new Array(displayCategories.length).fill(0)
              });
            }
          }
        });

        displaySeries = newSeries;
      }

      const hasZoom = displayCategories.length > 10;

      const legendRows: any[] = [];
      const itemsPerRow = displaySeries.length > 20 ? 8 : (displaySeries.length > 10 ? 6 : 5);
      for (let i = 0; i < displaySeries.length; i += itemsPerRow) {
        legendRows.push({
          data: displaySeries.slice(i, i + itemsPerRow).map((s) => s.name),
          top: Math.floor(i / itemsPerRow) * 22,
          left: 'center',
          itemGap: 16,
          itemWidth: 12,
          itemHeight: 12,
          textStyle: { fontSize: 11, color: '#262626' },
          icon: 'rect'
        });
      }

      const calculatedGridTop = Math.max(60, legendRows.length * 22 + 22);

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any[]) => {
            let total = 0;
            params.forEach((p) => (total += Number(p.value) || 0));
            let html = `<div style="font-weight:600;margin-bottom:6px;font-size:13px;">${params[0]?.name || ''} <span style="font-weight:normal;color:#666;">(Tổng: ${formatNumber(total)})</span></div>`;
            const activeItems = params.filter((p) => Number(p.value) > 0);
            if (activeItems.length === 0) {
              html += `<div style="color:#888;font-size:12px;">Số lượng: 0</div>`;
            } else {
              activeItems.forEach((p) => {
                const val = Number(p.value) || 0;
                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                html += `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin:3px 0;font-size:12px;">
                  <span>${p.marker} ${p.seriesName}</span>
                  <span style="font-weight:600;">${formatNumber(val)} <span style="font-weight:normal;color:#888;">(${pct}%)</span></span>
                </div>`;
              });
            }
            return html;
          }
        },
        legend: legendRows,
        grid: {
          top: calculatedGridTop,
          left: '3%',
          right: '4%',
          bottom: hasZoom ? 125 : 85,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: displayCategories,
          axisLabel: {
            interval: 0,
            rotate: 0,
            fontSize: displayCategories.length > 15 ? 10 : 11,
            lineHeight: displayCategories.length > 15 ? 12 : 14,
            align: 'center',
            verticalAlign: 'top',
            formatter: (val: string) => formatCategoryAxisLabel(val, displayCategories.length)
          }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#e8e8e8'
            }
          }
        },
        dataZoom: hasZoom
          ? [
            {
              type: 'slider',
              show: true,
              startValue: 0,
              endValue: 9,
              height: 18,
              bottom: 8,
              borderColor: 'transparent',
              backgroundColor: '#f5f5f5',
              fillerColor: 'rgba(24, 144, 255, 0.2)',
              handleStyle: {
                color: '#1890ff',
                borderColor: '#1890ff'
              }
            },
            { type: 'inside', startValue: 0, endValue: 9 }
          ]
          : [],
        series: displaySeries.map((s, idx) => {
          let itemColor: string | undefined;
          if (viewBy === 'province_health') {
            itemColor = PROVINCE_COLORS[s.name];
            if (!itemColor) {
              const matchedProv = Object.keys(PROVINCE_COLORS).find(p => isProvinceMatch(p, s.name) || isProvinceMatch(s.name, p));
              if (matchedProv) {
                itemColor = PROVINCE_COLORS[matchedProv];
              } else {
                itemColor = PROVINCE_PALETTE[idx % PROVINCE_PALETTE.length];
              }
            }
          } else {
            itemColor = AGE_GROUP_COLORS[s.name] || GENDER_COLORS[s.name];
            if (!itemColor) {
              const ageOptIdx = AGE_GROUPS_OPTIONS.findIndex((a) => a.label === s.name || a.value === s.name);
              if (ageOptIdx >= 0) {
                itemColor = AGE_GROUP_COLORS[AGE_GROUPS_OPTIONS[ageOptIdx].label] || COLOR_PALETTE_20[ageOptIdx % COLOR_PALETTE_20.length];
              } else {
                const fullIdx = (stackedChartData.series || []).findIndex((orig) => orig.name === s.name);
                const healthOptIdx = HEALTH_STATUS_OPTIONS.findIndex((h) => h.label === s.name || h.dbName === s.name);
                const fallbackIdx = fullIdx >= 0 ? fullIdx : (healthOptIdx >= 0 ? healthOptIdx : idx);
                itemColor = COLOR_PALETTE_20[fallbackIdx % COLOR_PALETTE_20.length];
              }
            }
          }
          return {
            name: s.name,
            type: 'bar',
            stack: 'total',
            barMaxWidth: 60,
            itemStyle: { color: itemColor },
            labelLayout: fitBarLabel,
            label: {
              overflow: 'truncate',
              ellipsis: '',
              show: true,
              position: 'inside',
              align: 'center',
              verticalAlign: 'middle',
              formatter: (params: any) => {
                const val = Number(params.value) || 0;
                if (val <= 0) return '';
                return formatNumber(val);
              },
              fontSize: BAR_LABEL_FONT_SIZE,
              fontWeight: 500,
              color: '#ffffff'
            },
            data: s.data
          };
        })
      };
    }

    if (viewBy === 'gender') {
      return {
        tooltip: {
          trigger: 'item',
          formatter: (params: any) =>
            `${params.name}: ${params.percent}% (${formatNumber(params.value)})`
        },
        legend: {
          orient: 'horizontal',
          top: 0,
          icon: 'rect',
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 24,
          textStyle: {
            fontSize: 13,
            color: '#262626'
          }
        },
        series: [
          {
            name: 'Giới tính',
            type: 'pie',
            radius: ['55%', '82%'],
            center: ['50%', '52%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 0,
              borderColor: 'transparent',
              borderWidth: 0
            },
            label: {
              show: true,
              position: 'inside',
              formatter: (params: any) =>
                params.value > 0
                  ? `${params.percent}%\n(${formatNumber(params.value)})`
                  : '',
              color: '#ffffff',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 16
            },
            labelLine: {
              show: false
            },
            emphasis: {
              disabled: true
            },
            data: filteredChartData.map((item) => {
              let color = '#bfbfbf';
              if (item.name === 'Nam' || item.code === 'MALE') {
                color = '#1890ff';
              } else if (item.name === 'Nữ' || item.code === 'FEMALE') {
                color = '#FAA5EB';
              }
              return {
                name: item.name,
                value: item.value,
                itemStyle: {
                  color
                }
              };
            })
          }
        ]
      };
    } else {
      // Basic Bar Chart
      const xCategories = filteredChartData.map((d) => d.name);
      const values = filteredChartData.map((d) => d.value);

      const hasZoom = filteredChartData.length > 10;

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            const p = params[0];
            const item = filteredChartData[p.dataIndex];
            return `<b>${p.name}</b><br/>Số lượng: ${formatNumber(p.value)} (${item?.percentage || 0}%)`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: hasZoom ? 125 : 85,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xCategories,
          axisLabel: {
            interval: 0,
            rotate: 0,
            fontSize: xCategories.length > 15 ? 10 : 11,
            lineHeight: xCategories.length > 15 ? 12 : 14,
            align: 'center',
            verticalAlign: 'top',
            formatter: (val: string) => formatCategoryAxisLabel(val, xCategories.length)
          }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#e8e8e8'
            }
          }
        },
        dataZoom: hasZoom
          ? [
            {
              type: 'slider',
              show: true,
              startValue: 0,
              endValue: 9,
              height: 18,
              bottom: 8,
              borderColor: 'transparent',
              backgroundColor: '#f5f5f5',
              fillerColor: 'rgba(24, 144, 255, 0.2)',
              handleStyle: {
                color: '#1890ff',
                borderColor: '#1890ff'
              }
            },
            {
              type: 'inside',
              startValue: 0,
              endValue: 9
            }
          ]
          : [],
        series: [
          {
            name: 'Số người dùng',
            type: 'bar',
            labelLayout: fitBarLabel,
            barMaxWidth: 60,
            itemStyle: {
              color: PRIMARY_BAR_COLOR,
              borderRadius: [2, 2, 0, 0]
            },
            label: {
              show: true,
              position: 'inside',
              overflow: 'truncate',
              ellipsis: '',
              formatter: (params: any) => {
                const val = Number(params.value) || 0;
                if (val <= 0) return '';
                return formatNumber(val);
              },
              fontSize: BAR_LABEL_FONT_SIZE,
              fontWeight: 'bold',
              color: '#ffffff'
            },
            data: values
          },
          {
            name: 'Tỷ lệ %',
            type: 'bar',
            barMaxWidth: 60,
            barGap: '-100%',
            itemStyle: {
              color: 'transparent'
            },
            tooltip: {
              show: false
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => {
                const item = filteredChartData[params.dataIndex];
                if (params.value === 0) return '';
                return `(${item?.percentage || 0}%)`;
              },
              fontSize: 11,
              color: '#333333'
            },
            data: values
          }
        ]
      };
    }
  };

  const getSubFilter1Config = () => {
    if (viewBy === 'age' || viewBy === 'age_gender' || viewBy === 'age_province' || viewBy === 'age_health') {
      return { label: 'Độ tuổi', mode: 'multiple' as const, options: AGE_GROUPS_OPTIONS, placeholder: 'Tất cả độ tuổi' };
    }
    if (viewBy === 'health_gender' || viewBy === 'health_status') {
      return {
        label: 'Tình trạng sức khỏe',
        mode: 'multiple' as const,
        useCheckbox: true,
        options: HEALTH_STATUS_OPTIONS,
        placeholder: 'Tất cả tình trạng sức khỏe',
      };
    }
    if (viewBy === 'province_gender' || viewBy === 'province_health' || viewBy === 'province') {
      return {
        label: 'Tỉnh thành',
        mode: 'multiple' as const,
        options: ALL_PROVINCE_OPTIONS,
        placeholder: 'Tất cả tỉnh thành',
      };
    }
    if (viewBy === 'gender') {
      return {
        label: 'Giới tính',
        mode: 'multiple' as const,
        options: [
          { value: 'MALE', label: 'Nam' },
          { value: 'FEMALE', label: 'Nữ' },
          { value: 'NO_INFO', label: 'Không có thông tin' }
        ],
        placeholder: 'Tất cả giới tính',
      };
    }
    return {
      label: 'Bộ lọc',
      mode: 'multiple' as const,
      options: chartData.map((item) => ({
        value: item.code || item.name,
        label: item.name
      })),
      placeholder: 'Tất cả'
    };
  };


  const getSubFilter2Config = () => {
    if (viewBy === 'age_gender' || viewBy === 'health_gender' || viewBy === 'province_gender') {
      return {
        label: 'Giới tính',
        mode: 'multiple' as const,
        options: [
          { value: 'MALE', label: 'Nam' },
          { value: 'FEMALE', label: 'Nữ' },
          { value: 'NO_INFO', label: 'Không có thông tin' },
        ],
        placeholder: 'Tất cả giới tính',
      };
    }
    if (viewBy === 'age_province') {
      return {
        label: 'Tỉnh thành',
        mode: 'multiple' as const,
        options: ALL_PROVINCE_OPTIONS,
        placeholder: 'Tất cả tỉnh thành',
      };
    }
    if (viewBy === 'age_health' || viewBy === 'province_health') {
      return {
        label: 'Tình trạng sức khỏe',
        mode: 'multiple' as const,
        useCheckbox: true,
        options: HEALTH_STATUS_OPTIONS,
        placeholder: 'Tất cả tình trạng sức khỏe',
      };
    }
    return null;
  };

  return (
    <Card css={cardStyle}>
      <div>
        {/* Chart Header Row */}
        <div css={headerStyle}>
          {/* Title & Sub-Filter Select(s) */}
          <div css={titleSectionStyle}>
            <Title level={4} style={{ margin: 0, marginBottom: 8, fontSize: 16 }}>
              {getChartTitle()}
            </Title>
            <div style={{ display: 'flex', gap: '16px', padding: '0 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isCombinedView && getSubFilter2Config() ? 1 : '0 0 50%' }}>
                <span style={{ fontSize: 14, color: '#000000', whiteSpace: 'nowrap' }}>
                  {getSubFilter1Config().label}
                </span>
                <Select
                  value={
                    subFilter === 'all'
                      ? []
                      : (Array.isArray(subFilter) ? subFilter : [subFilter])
                  }
                  onChange={(val) => onSubFilterChange(!val || val.length === 0 ? 'all' : val)}
                  allowClear={subFilter !== 'all' && (Array.isArray(subFilter) ? subFilter.length > 0 : true)}
                  mode="multiple"
                  maxTagCount="responsive"
                  showSearch
                  filterOption={matchSearchOption}
                  placeholder={getSubFilter1Config().placeholder}
                  style={{ width: '100%' }}
                  dropdownMatchSelectWidth={false}
                  options={getSubFilter1Config().options}
                  menuItemSelectedIcon={(getSubFilter1Config() as any).useCheckbox ? null : undefined}
                  optionRender={
                    (getSubFilter1Config() as any).useCheckbox
                      ? (option) => {
                          const isSelected = Array.isArray(subFilter) ? subFilter.includes(option.value as string) : subFilter === option.value;
                          return (
                            <Space>
                              <Checkbox checked={isSelected} style={{ pointerEvents: 'none' }} />
                              {option.label}
                            </Space>
                          );
                        }
                      : undefined
                  }
                />
              </div>
              {isCombinedView && getSubFilter2Config() && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <span style={{ fontSize: 14, color: '#000000', whiteSpace: 'nowrap' }}>
                    {getSubFilter2Config()!.label}
                  </span>
                  <Select
                    value={
                      subFilter2 === 'all' || !subFilter2
                        ? []
                        : (Array.isArray(subFilter2) ? subFilter2 : [subFilter2])
                    }
                    onChange={(val) => onSubFilter2Change?.(!val || val.length === 0 ? 'all' : val)}
                    allowClear={subFilter2 !== 'all' && subFilter2 !== undefined && (Array.isArray(subFilter2) ? subFilter2.length > 0 : true)}
                    mode="multiple"
                    maxTagCount="responsive"
                    showSearch
                    filterOption={matchSearchOption}
                    placeholder={getSubFilter2Config()!.placeholder}
                    style={{ width: '100%' }}
                    dropdownMatchSelectWidth={false}
                    options={getSubFilter2Config()!.options}
                    menuItemSelectedIcon={(getSubFilter2Config() as any).useCheckbox ? null : undefined}
                    optionRender={
                      (getSubFilter2Config() as any).useCheckbox
                        ? (option) => {
                            const isSelected = Array.isArray(subFilter2) ? subFilter2.includes(option.value as string) : subFilter2 === option.value;
                            return (
                              <Space>
                                <Checkbox checked={isSelected} style={{ pointerEvents: 'none' }} />
                                {option.label}
                              </Space>
                            );
                          }
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          </div>
          {/* Time Filters & RangePicker */}
          <div css={filterSectionStyle}>
            <Space size='small' wrap>
              <Button
                type={period === 'today' ? 'primary' : 'text'}
                onClick={() => onPeriodChange('today')}
              >
                Hôm nay
              </Button>
              <Button
                type={period === 'this_month' ? 'primary' : 'text'}
                onClick={() => onPeriodChange('this_month')}
              >
                Tháng này
              </Button>
              <Button
                type={period === 'this_year' ? 'primary' : 'text'}
                onClick={() => onPeriodChange('this_year')}
              >
                Năm này
              </Button>
              <Button
                type={period === 'all' ? 'primary' : 'text'}
                onClick={() => onPeriodChange('all')}
              >
                Tất cả
              </Button>
              <StatisticsDateRangePicker value={dateRange} onChange={onDateRangeChange}
                subject='profile' />
            </Space>
          </div>
        </div>
        {/* <div css={filterRowStyle}>
          
        </div> */}

        {/* Legend Indicator */}
        {['age', 'health_status', 'province'].includes(viewBy) && (
          <div css={legendStyle}>
            <span css={legendBoxStyle} />
            <Text type='secondary' style={{ fontSize: 13 }}>
              {getTotalLabel()}
            </Text>
          </div>
        )}

        {/* ECharts Area */}
        <Spin spinning={loading}>
          <ReactECharts
            ref={echartsRef}
            option={getOption()}
            style={{ height: '560px', width: '100%' }}
            notMerge={true}
            lazyUpdate={false}
          />
        </Spin>
      </div>
    </Card>
  );
});

ProfileCharts.displayName = 'ProfileCharts';

const cardStyle = css`
  margin-bottom: 16px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const headerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`;

const titleSectionStyle = css`
  display: flex;
  flex-direction: column;
`;

const filterSectionStyle = css`
  display: flex;
  justify-content: flex-end;
  padding-right: 12px;
`;


const legendStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const legendBoxStyle = css`
  width: 12px;
  height: 12px;
  background-color: ${PRIMARY_BAR_COLOR};
  border-radius: 2px;
  display: inline-block;
`;
